import { useQuery } from "@tanstack/react-query";
import { useState } from "react";

import { HeatMechanicalResult } from "../../models/HeatMechanicalResult";
import { getMechanicalResultVersions } from "../../repositories/CampaignRepository";
import {
	buildRunSnapshots,
	PLACEHOLDER_RUN_ID,
	RunGroup,
	RunSnapshot,
} from "./runGroups";

export type RunHistoryView = {
	group: RunGroup;
	at: Date | null;
	source: RunSnapshot["source"];
	kinds: RunSnapshot["kinds"];
	updatedFields: Map<string, string | null>;
	isLoading: boolean;
	position: number;
	total: number;
	isCurrent: boolean;
	back: () => void;
	forward: () => void;
	close: () => void;
};

export const useRunHistory = () => {
	const [viewing, setViewing] = useState<{
		runId: number;
		index: number | null;
	} | null>(null);

	const { data: versions, isLoading } = useQuery<HeatMechanicalResult[], Error>(
		{
			queryKey: ["mechanicalResultVersions", viewing?.runId],
			queryFn: ({ signal }) =>
				getMechanicalResultVersions(
					{ mechanicalTestRunId: viewing?.runId as number },
					signal,
				),
			enabled: !!viewing && viewing.runId !== PLACEHOLDER_RUN_ID,
		},
	);

	const isViewing = (runId: number) => viewing?.runId === runId;

	const toggle = (runId: number) =>
		setViewing(isViewing(runId) ? null : { runId, index: null });

	const close = () => setViewing(null);

	const viewOf = (group: RunGroup): RunHistoryView | null => {
		if (!isViewing(group.runId)) return null;

		const snapshots = buildRunSnapshots(group, versions ?? []);
		const idle = { isLoading, close, back: () => {}, forward: () => {} };

		if (snapshots.length === 0)
			return {
				...idle,
				group,
				at: null,
				source: "auto",
				kinds: [],
				updatedFields: new Map(),
				position: 0,
				total: 0,
				isCurrent: true,
			};

		const last = snapshots.length - 1;
		const index = Math.min(viewing?.index ?? last, last);
		const snapshot = snapshots[index];

		const step = (delta: number) =>
			setViewing({
				runId: group.runId,
				index: Math.max(0, Math.min(last, index + delta)),
			});

		return {
			...idle,
			group: snapshot.group,
			at: snapshot.at,
			source: snapshot.source,
			kinds: snapshot.kinds,
			updatedFields: snapshot.updatedFields,
			position: index + 1,
			total: snapshots.length,
			isCurrent: index === last,
			back: () => step(-1),
			forward: () => step(1),
		};
	};

	return { isViewing, toggle, close, viewOf };
};
